import {DateTime} from 'luxon';
import React, {FC, ReactNode, useEffect, useMemo, useState} from 'react';
import {concat, interval, noop, timer} from 'rxjs';

import {useEventHandler} from '../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {DataEventActionsEnum} from '../../../libs/shared/core/src/models/tracking/dataEventActions';
import {AppDataEventObject} from '../../../libs/shared/core/src/models/tracking/objectNames/appDataEventObject';
import {useDataEventTracking} from '../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  announcementSeenAction,
  permanentlyDismissIdAction,
} from '../../../libs/shared/core/src/store/data/stash/stashActions';
import {
  getIsAnnouncementThrottled,
  getIsStashInitialized,
  getPermanentlyDismissedIds,
  makeGetIsPermanentlyDismissedId,
} from '../../../libs/shared/core/src/store/data/stash/stashSelectors';
import {
  getHasCxSurveyAnnouncement,
  getHasTicketStatusesIdsAnnouncement,
} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppParametricSelector, AppSelector} from '../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector, useAppStore} from '../../helpers/appReduxHelpers';
import {getIsCustomer} from '../../store/data/company/companySelectors';
import {getIsUserNewish} from '../../store/data/teammates/teammatesSelectors';
import {AppAnnouncementTipProps, CloseClickOptions, CtaClickAfterOptions} from './announcementTipHelpers';
import {CxSurveyAnnouncementTip} from './cxSurvey/cxSurveyAnnouncementTip';
import {TicketStatusesIdsAnnouncementTip} from './ticketStatusesIds/ticketStatusesIdsAnnouncementTip';

/*
 * Types.
 */

interface AnnouncementTip {
  id: string;
  startsAt: number;
  expiresAt: number;
  getIsEligible: AppSelector<boolean>;
  renderTip: (props: AppAnnouncementTipProps) => ReactNode;
}

/*
 * Constants.
 */

const csSurvey2024Q4StartsAt = DateTime.fromISO('2024-11-19T09:00:00-0800');

const announcementTips: ReadonlyArray<AnnouncementTip> = [
  {
    id: 'ticketStatusesIds',
    getIsEligible: getHasTicketStatusesIdsAnnouncement,
    startsAt: DateTime.fromISO('2024-10-01T00:00:00-0700').toMillis(),
    expiresAt: DateTime.fromISO('2024-11-05T00:00:00-0700').toMillis(),
    renderTip: ({onCtaClickAfter, onCloseClick}) => (
      <TicketStatusesIdsAnnouncementTip onCtaClickAfter={onCtaClickAfter} onCloseClick={onCloseClick} />
    ),
  },
  {
    id: 'cxSurvey2024Q4',
    getIsEligible: getHasCxSurveyAnnouncement,
    startsAt: csSurvey2024Q4StartsAt.toMillis(),
    expiresAt: csSurvey2024Q4StartsAt.plus({weeks: 2}).toMillis(),
    renderTip: ({onCtaClickAfter, onCloseClick}) => (
      <CxSurveyAnnouncementTip onCtaClickAfter={onCtaClickAfter} onCloseClick={onCloseClick} />
    ),
  },
];

/*
 * Selectors.
 */

const getNextEligibleAnnouncementTip: AppParametricSelector<{now: number}, AnnouncementTip | undefined> = (
  state,
  {now},
) => {
  // Never show announcements to trials or users in their first 30 days.
  if (!getIsCustomer(state) || getIsUserNewish(state)) {
    return undefined;
  }

  // Limit number of announcements shown in a given time frame.
  if (getIsAnnouncementThrottled(state, {now})) {
    return undefined;
  }

  // Find the first active, eligible, not dismissed announcement.
  const permanentlyDismissedIds = getPermanentlyDismissedIds(state);
  return announcementTips.find(
    (tip) =>
      tip.startsAt <= now &&
      now < tip.expiresAt &&
      tip.getIsEligible(state) &&
      !permanentlyDismissedIds.contains(buildAnnouncementDismissId(tip.id)),
  );
};

/*
 * Component.
 */

export const AppAnnouncementTipContainer: FC = () => {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();

  // We want to make sure we know which announcements the user has dismissed before we start showing any.
  const isStashInitialized = useAppSelector(getIsStashInitialized);

  const [eligibleAnnouncementTip, setEligibleAnnouncementTip] = useState<AnnouncementTip | undefined>();
  const getIsDismissed = useMemo(
    () => makeGetIsPermanentlyDismissedId(buildAnnouncementDismissId(eligibleAnnouncementTip?.id)),
    [eligibleAnnouncementTip?.id],
  );
  const isDismissed = useAppSelector(getIsDismissed);
  const visibleAnnouncementTip =
    eligibleAnnouncementTip !== undefined && !isDismissed ? eligibleAnnouncementTip : undefined;

  useEffect(() => {
    if (!isStashInitialized || visibleAnnouncementTip) {
      return noop;
    }

    const subscription = concat(timer(2 * 1000), interval(60 * 1000)).subscribe(() => {
      setEligibleAnnouncementTip(getNextEligibleAnnouncementTip(store.getState(), {now: Date.now()}));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [store, isStashInitialized, visibleAnnouncementTip]);

  const onView = useEventHandler((announcementTip: AnnouncementTip | undefined) => {
    if (!announcementTip) {
      return;
    }

    const {id} = announcementTip;
    trackDataEvent(DataEventActionsEnum.VIEW, AppDataEventObject.ANNOUNCEMENT, {id});
  });

  // Track when the announcement is shown.
  useEffect(() => {
    onView(visibleAnnouncementTip);
  }, [visibleAnnouncementTip, onView]);

  if (!visibleAnnouncementTip) {
    return null;
  }

  const {id, renderTip} = visibleAnnouncementTip;

  const close = () => {
    dispatch(announcementSeenAction());
    dispatch(permanentlyDismissIdAction(buildAnnouncementDismissId(id)));
  };

  // Track when some non-dismiss action is clicked.
  const onCtaClickAfter = (_event: unknown, options: CtaClickAfterOptions = {}) => {
    const {shouldClose = false, trackingData = {}} = options;
    if (shouldClose) {
      close();
    }
    trackDataEvent(DataEventActionsEnum.CLICK, AppDataEventObject.ANNOUNCEMENT, {...trackingData, id});
  };

  // Track when some dismiss action is clicked.
  const onCloseClick = (_event: unknown, options: CloseClickOptions = {}) => {
    const {trackingData = {}} = options;
    close();
    trackDataEvent(DataEventActionsEnum.CLOSE, AppDataEventObject.ANNOUNCEMENT, {...trackingData, id});
  };

  return renderTip({onCtaClickAfter, onCloseClick});
};

/*
 * Helpers.
 */

function buildAnnouncementDismissId(tipId: string): string;
function buildAnnouncementDismissId(tipId: string | undefined): string | undefined;
function buildAnnouncementDismissId(tipId: string | undefined): string | undefined {
  if (!tipId) {
    return undefined;
  }

  return `${tipId}Announcement`;
}
